<template lang="pug">
BriqDrawer(
  :isBriqDrawerOpen="isReportDrawerOpen"
  :title="title"
  :description="description"
  @closeBriqDrawer="closeReportDrawer"
)
  BriqAlert(v-show="exportNotification" variant="info" column)
    span(v-html="exportNotification")

  form(ref="form" :action="endpoint" method="get" @submit.prevent="submit" class='form')
    BriqRadio(
      v-if="filterBy && filterBy.timezone"
      label="Time zone"
      :name="`${name}-timezone`"
      radioButtonStyle="grey"
      :options="timezoneOptions"
      :optionsDescription="timezoneOptionsDescriptions"
      v-model="timezone"
      :hide-optional-marker="true"
      :largerLabel="true")
    DateTypeWithDateRangeSelector(
      v-if="filterBy && filterBy.dateType"
      label="Moment of"
      :name="name"
      :dateTypesAllowed="filterBy.dateType"
      :customDateRangeLimit="customDateRangeLimit"
      :dateRangeDisabledDates="dateRangeDisabledDates"
      :maxDate="maxDate"
      @periodSelected="getPeriodSelected"
    )
    DateRangeSelector(
      v-if="filterBy && filterBy.daterange"
      label="Date range"
      :name="name"
      :dateRangesAllowed="filterBy.daterange"
      :customDateRangeLimit="customDateRangeLimit"
      :dateRangeDisabledDates="dateRangeDisabledDates"
      :maxDate="maxDate"
      @periodSelected="getPeriodSelected"
    )
    BriqRadio.mb-S(
      v-if="filterBy && filterBy.separator"
      label="Separator"
      :name="`${name}-separator`"
      radioButtonStyle="grey"
      :options="separatorOptions"
      :optionsDescription="separatorOptionsDescriptions"
      v-model="separator"
      :hide-optional-marker="true"
      :largerLabel="true")
    BriqMultiselect.mb-S(
      v-if="filterBy && filterBy.dataCulture"
      label="Data culture"
      v-model="selectedDataCulture"
      name="dataCulture"
      :valueSetter="culture => culture.cultureCode"
      :options="dataCultures"
      display="name"
      track-by="cultureCode"
      )
    BriqMultiselect.mb-S(
      v-if="filterBy && filterBy.reportType"
      label="Report type"
      v-model="selectedReportType"
      name="reportType"
      :valueSetter="reportType => reportType.key"
      :options="reportTypeOptions"
      display="name"
      track-by="key"
      :required="true"
      )
    BriqInput.mb-S(
      v-if="briqPayExport"
      label="E-mail address"
      v-model="emailAddress"
      type="email"
      name="recipientEmail"
      hide-optional-marker
      :required="true"
      :error="emailError"
      )
    BriqButton.download-button(
      variant="main"
      submit
      :disabled="fetching"
      )
      BriqIcon(:name="briqPayExport ? 'icon-mail-icon' : 'icon-download_icon'")
      span {{ buttonName }}
</template>
<script>
import BriqDrawer from '@v/components/BriqDrawer.vue'
import BriqAlert from '../components-global/BriqAlert.vue'
import DateTypeWithDateRangeSelector from './DateRangeSelectors/DateTypeWithDateRangeSelector.vue'
import DateRangeSelector from './DateRangeSelectors/DateRangeSelector.vue'
import { subDays, subMonths, addDays, format, startOfMonth, endOfMonth } from 'date-fns'
import { getTimezoneOffset, formatTimezoneOptionsDescription } from './helpers.js'
import { periodIsValid } from '@/helpers/flatpickrPeriodValidator.js'
import { mapGetters } from 'vuex'
import { email, required } from 'vuelidate/lib/validators'

export default {
  name: 'ReportDrawer',
  components: { BriqDrawer, BriqAlert, DateTypeWithDateRangeSelector, DateRangeSelector },
  props: {
    title: String,
    name: String,
    description: String,
    endpoint: String,
    periodIsRequired: Boolean,
    localTimezone: String,
    maxDate: String,
    exportNotification: String,
    separatorValues: {
      type: Array,
      required: true,
    },
    defaultSeparatorValue: String,
    filterBy: {
      type: Object,
      default: function() {
        return {
          timezone: true,
          daterange: {
            yesterday: true,
            lastSevenDays: true,
            previousMonth: true,
          },
          separator: true,
          dataCulture: true,
        }
      },
    },
    customDateRangeLimit: {
      default: null,
      type: Number,
    },
    dateRangeDisabledDates: {
      default: null,
      type: Array
    }
  },
  data() {
    return {
      period: '',
      clickedSubmit: false,
      isReportDrawerOpen: false,
      timezone: 'local',
      timezoneOptions: {
        local: 'Local time',
        utc: 'Server Time',
      },
      timezoneOptionsDescriptions: {
        local: '',
        utc: 'UTC',
      },
      separator: '',
      selectedDataCulture: '',
      selectedReportType: 'Balance',
      reportTypeOptions: [
        { key: 'Balance', name: 'Balance' },
        { key: 'Payout', name: 'Payout' },
      ],
      emailAddress: '',
      fetching: false,
    }
  },
  validations: {
    emailAddress: {
      required,
      email,
    },
  },
  computed: {
    ...mapGetters('dataCultures', ['dataCultures']),
    ...mapGetters('briqState', ['businessUnitDataCulture']),
    buttonName() {
      return this.briqPayExport ? 'E-mail export' : 'Download export'
    },
    periodIsValid() {
      return periodIsValid(this.period)
    },
    canSubmit() {
      return !this.periodIsRequired || this.periodIsValid
    },
    separatorOptions() {
      const separatorOptions = {}

      this.separatorValues.forEach(function(separator) {
        separatorOptions[separator.Name] = separator.DisplayName
      })

      return separatorOptions
    },
    separatorOptionsDescriptions() {
      const separatorOptionsDescriptions = {}

      this.separatorValues.forEach(function(separator) {
        separatorOptionsDescriptions[separator.Name] = separator.Description
      })

      return separatorOptionsDescriptions
    },
    briqPayExport() {
      return this.name === 'briqPayExport'
    },
    emailError() {
      const error = this.$v.$dirty && this.$v.emailAddress.$invalid && 'Email address is required'
      return error || null
    },
  },
  methods: {
    openReportDrawer() {
      this.isReportDrawerOpen = true
    },
    closeReportDrawer() {
      this.isReportDrawerOpen = false
    },
    async submit() {
      this.fetching = true
      this.$v.$touch()

      if (this.briqPayExport && this.$v.$invalid) this.$notifyError('Invalid Email Addres!')
      else {
        this.clickedSubmit = true
        if (this.canSubmit) {
          if (this.briqPayExport) {
            try {
              const form = new FormData(this.$refs['form'])
              const response = await this.$apiClient.post(this.endpoint, form)
              if (response.status === 200)
                this.$notifySuccess(
                  'The report is being compiled, once it is ready, it will arrive in your mailbox',
                )
              else this.$notifyError('Something went wrong')
            } catch (e) {
              console.error(e)
              this.$notifyError('Something went wrong')
            }
          } else {
            this.$refs['form'].submit()
          }
        }
      }
      this.fetching = false
    },
    getPeriodSelected(period) {
      this.period = period
    },
  },
  watch: {
    businessUnitDataCulture(v) {
      this.selectedDataCulture = v
    },
  },
  mounted() {
    const localTimezoneHours = getTimezoneOffset(this.localTimezone, new Date())
    this.timezoneOptionsDescriptions.local = formatTimezoneOptionsDescription(localTimezoneHours)
    this.separator =
      this.defaultSeparatorValue !== '' ? this.defaultSeparatorValue : this.separatorValues[0].Name
  },
}
</script>
<style scoped lang="stylus">
.form {
  margin-top: 12px
}
.icon-download_icon
  margin-right 10px

.download-button
  span
    margin-left 5px
</style>
